import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import dayjs, { Dayjs } from "dayjs";

import { GridColDef } from "@mui/x-data-grid";
import { AppDataGrid } from "../../../../../components/datagrid/AppDataGrid";

import { Controller, useForm } from "react-hook-form";
import alertService from "../../../../../components/alert/alertService";
import { EcoEfficiencyContext } from "../../../../../contexts/EcoEfficiencyContext";
import { common, input } from "../../../../../i18n";
import {
  MonthlyComponentEntry,
  EcoEffActualRequest,
} from "../../../../../models/dto/response/ResponseEcoEfficiency";
import { EcoEfficiencyService } from "../../../../../service/eco-efficiency.service";
import { tableStyle } from "../../../../emission/_components/data-table/EmissionDataTableStyle";
import { EconomicIndicatorComponent } from "../../../../../models/dto/response/ResponseCreateEconomicIndicators";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { textFieldEndAlignWithFloatingHelper } from "../plan-setting/PlanSettingPercentChangesTable";
import { NumericFormat } from "react-number-format";

interface Props {
  monthlyValue: MonthlyComponentEntry[];
}

interface MonthlyItem {
  text: string;
  status: "green" | "yellow" | "gray";
  numOfmonth: number;
  year: number;
}

const colorMap: Record<string, string> = {
  green: "#00A36C",
  yellow: "#FFB703",
  gray: "#E0E0E0",
  red: "#FF4747",
};

const ActualDataMonth = ({ monthlyValue }: Props) => {
  const { t, i18n } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const { editData, getEcoEfficiency } = EcoEfficiencyContext();

  const [monthly, setMonthly] = useState<MonthlyItem[]>([]);

  const [startDate, setStartDate] = useState<Dayjs>(dayjs());

  const [endDate, setEndDate] = useState<Dayjs>(dayjs());

  const [rows, setRows] = useState<EconomicIndicatorComponent[]>([]);

  const [selectedMonth, setSelectedMonth] = useState<number>(0);

  const updateValue = async (
    row: EconomicIndicatorComponent,
    value: string,
  ) => {
    let temp: MonthlyComponentEntry[] = [...monthlyValue];

    const index = temp[selectedMonth].components.findIndex(
      (c) => c.economiccomponentsRef === row._id,
    );

    const oldValue = temp[selectedMonth].components[index].value;

    if (Number(oldValue ?? value + 1) === Number(value)) return;

    temp[selectedMonth].components[index].value = Number(value);

    const request: EcoEffActualRequest = {
      components: [
        {
          componentRef:
            temp[selectedMonth].components[index].economiccomponentsRef,
          ecoEffActuals: [
            {
              year: temp[selectedMonth].year,
              monthly: [
                { numOfmonth: temp[selectedMonth].month, value: Number(value) },
              ],
            },
          ],
        },
      ],
    };

    alertService.showLoading();

    try {
      const result = await EcoEfficiencyService.updateEcoefficiencyActual(
        request,
        editData!._id,
      );

      if (result) {
        const monthlyTemp = monthly.map((m, index) => {
          const status: "gray" | "yellow" | "green" = temp[
            index
          ].components.every((c) => c.value !== null && c.value !== undefined)
            ? "green"
            : "yellow";

          m.status = m.status === "gray" ? m.status : status;

          return m;
        });

        setMonthly(monthlyTemp);

        getEcoEfficiency();

        alertService.mixin(t("edit-successfully"));
      }
    } catch {
      // error alert already shown by service
    } finally {
      alertService.closeLoading();
    }
  };

  const {
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useForm({
    reValidateMode: "onChange",
    mode: "all",
  });

  const columns: GridColDef<EconomicIndicatorComponent>[] = [
    {
      field: "id",
      headerName: t("ID"),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return `PV-${
          params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
        }`;
      },
    },
    {
      field: "baseYear",
      headerName: t("Date"),
      minWidth: 80,
      align: "left",
      valueGetter: (value, row) =>
        dayjs(
          `${monthly[selectedMonth].year}-${monthly[selectedMonth].numOfmonth}`,
        )
          .locale(i18n.language)
          .format("MMM YYYY"),
    },
    {
      field: "nameTh",
      headerName: t("Indicator-Name-Table"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1,
      valueGetter: (value, row) =>
        i18n.language === "th"
          ? editData?.economicindicatorsRef.nameTh
          : editData?.economicindicatorsRef.nameEn,
    },
    {
      field: "nameEn",
      headerName: t("Components"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1,
      valueGetter: (value, row) =>
        i18n.language === "th" ? row.nameTh : row.nameEn,
    },
    {
      field: "",
      headerName: `${t("Amount-of-Product-Value-Indicator")}`,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      flex: 1,
      minWidth: 200,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              ...textFieldEndAlignWithFloatingHelper,
            }}
          >
            {/* <TextField
              key={params.row._id}
              {...register(
                params.row._id,
                getFieldConfig(params.row._id, params.row, {
                  t,
                  setValue,
                  getValues,
                  updateValue,
                  errors,
                })
              )}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              onKeyUp={async (event: React.KeyboardEvent) => {
                switch (event.key) {
                  case "Enter":
                    (document.activeElement as HTMLElement).blur();
                    break;
                  default:
                    break;
                }
              }}
              helperText={
                errors[params.row._id] &&
                errors[params.row._id]?.message?.toString()
              }
              error={errors[params.row._id] && true}
              type="text"
              sx={{
                textAlign: "end",
              }}
              placeholder={t("Enter-Value-Placeholder")}
              defaultValue={null}
            /> */}

            <Controller
              name={params.row._id}
              key={params.row._id}
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <NumericFormat
                  name={params.row._id}
                  key={params.row._id}
                  customInput={TextField}
                  thousandSeparator=","
                  decimalScale={2}
                  allowNegative={true}
                  allowLeadingZeros={false}
                  fixedDecimalScale
                  value={field.value ?? ""}
                  onValueChange={(values) => {
                    field.onChange(values.floatValue ?? null);
                  }}
                  onBlur={(v) => {
                    const value = getValues(params.row._id);

                    if (
                      !errors[params.row._id] &&
                      value != null &&
                      value !== "" &&
                      !isNaN(Number(value))
                    ) {
                      if (updateValue) updateValue(params.row, value);
                    }
                  }}
                  placeholder={t("Enter-Value-Placeholder")}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                  onKeyUp={async (event: React.KeyboardEvent) => {
                    switch (event.key) {
                      case "Enter":
                        (document.activeElement as HTMLElement).blur();
                        break;
                      default:
                        break;
                    }
                  }}
                />
              )}
            />
          </Box>
        );
      },
    },
    {
      field: "unit",
      headerName: t("Unit"),
      headerAlign: "left",
      minWidth: 140,
    },
  ];

  useEffect(() => {
    if (editData && monthlyValue.length > 0) {
      const startDate: Dayjs = dayjs(editData?.startDate);

      const endDate: Dayjs = dayjs(editData?.endDate);

      setStartDate(startDate);

      setEndDate(endDate);

      let temp: MonthlyItem[] = [];

      for (let i = 0; i < 12; i++) {
        const item = startDate.add(i, "M");

        //check status
        const status: "gray" | "yellow" | "green" = monthlyValue[
          i
        ].components.every((c) => c.value !== null && c.value !== undefined)
          ? "green"
          : "yellow";

        temp.push({
          text: `${startDate
            .add(i, "M")
            .locale(i18n.language)
            .format("MMM YY")}`,
          status: item.isAfter(dayjs()) ? "gray" : status,
          numOfmonth: item.month() + 1,
          year: item.year(),
        });
      }

      setMonthly(temp);

      setRows(editData.economicindicatorsRef.components);
    }
  }, [editData, monthlyValue]);

  useEffect(() => {
    try {
      const temp = monthlyValue[selectedMonth];

      temp.components.forEach((r) => {
        setValue(r.economiccomponentsRef, r.value ?? null);
      });
    } catch (error) {
      console.error(error);
    }
  }, [selectedMonth, monthlyValue]);

  return (
    <>
      <Box marginTop={"8px"} marginBottom={"20px"}>
        <Typography
          variant="body1"
          bgcolor={"var(--gray3)"}
          padding={"12px"}
          display={"flex"}
          alignItems={"center"}
          gap={"4px"}
          borderRadius={"4px"}
          color={"var(--green)"}
        >
          <InfoOutlinedIcon color="primary" />
          {t("Enter-Monthly-Product-Value")}
        </Typography>
      </Box>
      <TableContainer
        sx={{
          border: "1px solid var(--gray3)",
        }}
      >
        <Table
          sx={{
            ".MuiTableCell-root": {
              padding: "10px",
            },
          }}
        >
          <TableHead
            sx={{
              ".MuiTableCell-root": {
                padding: "10px",
              },
            }}
          >
            <TableRow
              sx={{
                background: "var(--gray3)",
              }}
            >
              <TableCell align="center">{t("Period")}</TableCell>
              {monthly.map((month, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    background:
                      selectedMonth === index ? "var(--gray9)" : undefined,
                    color: selectedMonth === index ? "var(--white)" : undefined,
                  }}
                >
                  {month.text}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              ".MuiTableCell-root": {
                padding: "0px 10px 0px 10px",
              },
            }}
          >
            <TableRow sx={{ height: "48px" }}>
              <TableCell align="center">
                <Typography variant="body1">{`${startDate.format(
                  "MMM YY",
                )} - ${endDate.format("MMM YY")}`}</Typography>
              </TableCell>

              {monthly.map((m, index) => (
                <TableCell
                  key={`${m}-${index}`}
                  align="center"
                  sx={{
                    cursor: "pointer",
                    background:
                      selectedMonth === index ? "var(--gray2)" : undefined,
                  }}
                  onClick={() => setSelectedMonth(index)}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: colorMap[m.status],
                      margin: "0 auto",
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box marginTop={"20px"}>
        <AppDataGrid
          rows={rows}
          columns={columns}
          rowHeight={90}
          sx={{ ...tableStyle() }}
          getRowId={(row) => row._id}
          disableMultipleRowSelection={true}
          hideFooter
          autoHeight
        />
      </Box>
    </>
  );
};

export default ActualDataMonth;
